import { isString } from 'lodash-es';

type StorageType = 'localStorage' | 'sessionStorage';
type GetStorageResult<T> = T extends undefined
  ? string | Record<string | number, unknown> | null | undefined
  : T;

interface StorageOptions {
  expires?: string | Date;
}

export const getStorageValue = <T>(
  storage: StorageType,
  key: string,
  defaultValue?: T
): GetStorageResult<T> => {
  let json;
  let result;

  if (window[storage]) {
    try {
      json = window[storage].getItem(key);
    } catch (e) {
      console.warn(e, `${storage} error`);

      return (defaultValue || null) as GetStorageResult<T>;
    }
  }

  try {
    result = JSON.parse(json as string);
  } catch {
    result = json;
  }

  if (
    result &&
    typeof result === 'object' &&
    'data' in result &&
    'expires' in result
  ) {
    const { data, expires } = result as { data: string; expires: string };

    if (new Date(expires) >= new Date()) {
      try {
        result = JSON.parse(data);
      } catch {
        result = data;
      }
    } else {
      result = null;
    }
  }

  return result !== undefined && result !== null
    ? result
    : (defaultValue as GetStorageResult<T>);
};

export const setStorageValue = (
  storage: StorageType,
  key: string,
  newValue: unknown,
  options: StorageOptions = {}
): void => {
  if (key && window[storage]) {
    const { expires } = options;
    let data: string;

    if (expires) {
      data = JSON.stringify({
        data: isString(newValue) ? newValue : JSON.stringify(newValue),
        expires: expires instanceof Date ? expires.toISOString() : expires
      });
    } else {
      data = isString(newValue) ? newValue : JSON.stringify(newValue);
    }

    try {
      window[storage].setItem(key, data);
    } catch (e) {
      console.warn(e, `${storage} error`);
    }
  }
};

export const removeStorageValue = (storage: StorageType, key: string): void => {
  if (key && window[storage]) {
    try {
      window[storage].removeItem(key);
    } catch (e) {
      console.warn(e, `${storage} error`);
    }
  }
};

// Aliases for localStorage and sessionStorage
export const getLocalStorageValue = <T>(key: string, defaultValue?: T) =>
  getStorageValue('localStorage', key, defaultValue);

export const setLocalStorageValue = (
  key: string,
  newValue: unknown,
  options?: StorageOptions
): void => setStorageValue('localStorage', key, newValue, options);

export const removeLocalStorageItem = (key: string): void =>
  removeStorageValue('localStorage', key);

export const getSessionStorageValue = <T>(key: string, defaultValue?: T) =>
  getStorageValue('sessionStorage', key, defaultValue);

export const setSessionStorageValue = (
  key: string,
  newValue: unknown,
  options?: StorageOptions
): void => setStorageValue('sessionStorage', key, newValue, options);

export const removeSessionStorageItem = (key: string): void =>
  removeStorageValue('sessionStorage', key);
